/**
 * Öffentliche Darstellungswahl (Paket S, 2026-09-10).
 *
 * Läuft BLOCKIEREND im <head> von `src/layouts/PublicLayout.astro`, damit
 * `html[data-reading]` vor dem ersten Paint steht. Ohne gemerkte Wahl folgt die
 * Darstellung der Geräteeinstellung (`prefers-color-scheme`, Ownerentscheid
 * 06.10.2026, vorher fest Hell). Gespeichert wird nur nach „Merken“ in der
 * Rückfrage (Variante B), nur `light`/`dim`, nie Formulardaten.
 *
 * Unabhängig vom Plattform-Theme (`data-theme`, Cookie `wolf-theme`,
 * /scripts/theme-no-flash.js): eigener Schlüssel, eigenes Attribut, kein Cookie.
 *
 * Als einziges blockierendes Skript des Rahmens registriert die Datei außerdem die
 * Beobachtung abgebrochener View Transitions (siehe unten): nur hier vor `pagereveal`.
 *
 * Die Datei liegt bewusst unter public/scripts (CSP `script-src 'self'`); sie
 * importiert nichts und liest ihre Texte aus dem Markup (`data-label-*`).
 */
(() => {
  "use strict";
  const root = document.documentElement;
  // Skriptfähigkeit vor dem ersten Paint markieren (.wa-js-only / .wa-no-js, statische No-JS-Navigation).
  root.classList.replace('no-js', 'js');
  // Dokumentübergreifende View Transitions (Anmeldung in styles/public/additions.css wie im Bestand):
  // Chromium bricht den Übergang bei sehr großen gemalten Seiten oder einem langsamen neuen Dokument ab.
  // Der Abbruch ist normal und sichtbar folgenlos; ohne Handler wäre er eine unbehandelte Ablehnung
  // („Transition was skipped“) in der Konsole und im Prüfharness. Die Zusagen werden hier bewusst
  // beobachtet — und zwar in diesem blockierenden Kopfskript: `pagereveal` feuert vor dem ersten
  // Rendern, bei langen Dokumenten also vor den defer-Skripten (gemessen an der Web-Ratgeberübersicht
  // unter Last: pagereveal 114 ms, defer-Registrierung 340 ms). Wird der Übergang schon vor
  // `pagereveal` verworfen, gibt es kein ViewTransition-Objekt mehr; der Browser meldet die Ablehnung
  // trotzdem im NEUEN Dokument. Nur genau dieser AbortError wird als behandelt markiert — sonst nichts.
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    if (reason && reason.name === 'AbortError' && /transition was skipped/i.test(String(reason.message))) event.preventDefault();
  });
  for (const type of ['pageswap', 'pagereveal']) {
    window.addEventListener(type, event => {
      const transition = event.viewTransition;
      if (!transition) return;
      for (const promise of [transition.ready, transition.finished, transition.updateCallbackDone]) promise?.catch(() => {});
    });
  }
  // Private Testmail-Seiten (DE/EN), der native Widerrufsbeleg und abgewiesene
  // Messstarts auf Ergebnisadressen laden diesen Rahmen nicht.
  // Den Übergang gezielt vor dem Wechsel überspringen: Das neue Dokument kann dessen
  // Ablehnung nicht mit diesem Skript beobachten. Der eigentliche Seitenwechsel bleibt erhalten.
  window.addEventListener('pageswap', event => {
    const url = event.activation?.entry?.url;
    if (!event.viewTransition || !url) return;
    const path = new URL(url).pathname;
    if (/^\/(?:en\/)?(?:entry\/|private(?:\/|$)|tools\/(?:web|email|api)-security-check\/results\/)/.test(path) || path === '/api/service-withdrawal') event.viewTransition.skipTransition();
  });
  if (!root.hasAttribute('data-appearance-capable')) return;
  const key = 'wa-public-reading-v1';
  // Ohne gemerkte Wahl folgt die Darstellung der Hell-/Dunkel-Einstellung des Geräts (Ownerentscheid 06.10.2026).
  // Die Einstellung wird nur abgefragt, nie gespeichert.
  const systemQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  const systemReading = () => (systemQuery && systemQuery.matches ? 'dim' : 'light');
  const stored = () => {
    try { const value = localStorage.getItem(key); return value === 'dim' || value === 'light' ? value : null; }
    catch { return null; }
  };
  const readPreference = () => {
    try {
      const value = localStorage.getItem(key);
      return value === 'dim' || value === 'light' ? value : systemReading();
    } catch { return root.dataset.reading === 'dim' || root.dataset.reading === 'light' ? root.dataset.reading : systemReading(); }
  };
  root.dataset.reading = readPreference();
  document.addEventListener('DOMContentLoaded', () => {
    const button = document.getElementById('reading-mode');
    const buttons = [...document.querySelectorAll('[data-reading-toggle]')];
    const state = document.getElementById('reading-mode-state');
    // Rückfrage im Moment der Wahl (Variante B, Ownerentscheid 06.10.2026): gespeichert wird erst nach „Merken“.
    const askElement = document.querySelector('[data-reading-ask]');
    const ask = askElement && typeof askElement.querySelector === 'function' ? askElement : null;
    let lastControl = button;
    // Die K7-Startschale liest dieselbe Wahl, auch ohne eigenen Bedienknopf.
    const texte = button?.dataset || {};
    const stateText = () => root.dataset.reading === 'dim' ? (texte.stateDim || 'Darstellung: Dunkel') : (texte.stateLight || 'Darstellung: Hell');
    const sync = () => {
      const dark = root.dataset.reading === 'dim';
      buttons.forEach(control => {
        const label = control.querySelector('[data-reading-label]');
        const next = dark ? (control.dataset.labelLight || 'Hell anzeigen') : (control.dataset.labelDim || 'Dunkel anzeigen');
        if (label) label.textContent = next;
        const short = control.querySelector('[data-reading-short]');
        if (short) short.textContent = dark ? (control.dataset.shortLight || 'Hell') : (control.dataset.shortDim || 'Dunkel');
        control.title = next;
      });
      const remembered = stored() !== null;
      document.querySelectorAll('[data-reading-reset]').forEach(control => { control.hidden = !remembered; });
      if (state) state.textContent = stateText();
    };
    const hideAsk = () => { if (ask) ask.hidden = true; };
    const showAsk = control => {
      if (!ask) return;
      const host = control && typeof control.closest === 'function' ? control.closest('.wa-reading-switch') : null;
      if (host && typeof host.appendChild === 'function' && ask.parentElement !== host) host.appendChild(ask);
      const current = ask.querySelector('[data-reading-ask-state]');
      if (current) current.textContent = root.dataset.reading === 'dim' ? (ask.dataset.stateDim || 'Dunkel ist aktiv.') : (ask.dataset.stateLight || 'Hell ist aktiv.');
      ask.hidden = false;
    };
    const answer = remember => {
      if (remember) { try { localStorage.setItem(key, root.dataset.reading); } catch { /* Ohne Ablage gilt die Wahl für diese Seite. */ } }
      hideAsk();
      sync();
      if (state && remember && ask?.dataset.saved) state.textContent = `${stateText()}. ${ask.dataset.saved}`;
      lastControl?.focus?.();
    };
    ask?.querySelector('[data-reading-remember]')?.addEventListener('click', () => answer(true));
    ask?.querySelector('[data-reading-once]')?.addEventListener('click', () => answer(false));
    ask?.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault?.(); answer(false); } });
    // Gleicht die Leiste beim Scrollen sofort mit der Lage des Kopfs ab (unten gesetzt, falls es Kopf und Leiste gibt).
    let refreshDock = null;
    // Widerruf so einfach wie das Merken: „Wahl vergessen“ in der Datenschutzerklärung und im Fuß jeder Seite.
    document.querySelectorAll('[data-reading-reset]').forEach(control => control.addEventListener('click', () => {
      try { localStorage.removeItem(key); } catch { /* The current view still resets. */ }
      root.dataset.reading = systemReading();
      hideAsk();
      sync();
      // Der Knopf verschwindet mit der gemerkten Wahl. Der Fokus geht an die sichtbare Darstellungstaste
      // (Leiste beim Scrollen, sonst Kopf), ohne die Seite zu verschieben; die Statusregion meldet das Entfernen.
      // Die Beobachtung meldet den Kopf erst nach dem nächsten Bild: Bei einem Klick direkt nach dem
      // Scrollen wäre die Leiste noch verborgen und der Fokus läge auf der Kopftaste außerhalb des Bildschirms.
      refreshDock?.();
      const dock = document.querySelector('[data-reading-dock]');
      const target = (dock && !dock.hidden && typeof dock.querySelector === 'function' && dock.querySelector('[data-reading-toggle]')) || button;
      target?.focus?.({ preventScroll: true });
      if (state && ask?.dataset.forgotten) state.textContent = `${stateText()}. ${ask.dataset.forgotten}`;
    }));
    buttons.forEach(control => control.addEventListener('click', () => {
      lastControl = control;
      root.dataset.reading = root.dataset.reading === 'dim' ? 'light' : 'dim';
      if (stored() !== null) {
        // Bereits gemerkt: die neue Wahl ersetzt den gemerkten Wert ohne erneute Rückfrage.
        try { localStorage.setItem(key, root.dataset.reading); } catch { /* Keine Speicherabhängigkeit. */ }
        hideAsk();
      } else if (root.dataset.reading !== systemReading()) showAsk(control);
      else hideAsk();
      sync();
      if (ask && !ask.hidden && state && ask.dataset.announce) state.textContent = `${stateText()}. ${ask.dataset.announce}`;
    }));
    // Ohne gemerkte Wahl folgt die geöffnete Seite einer geänderten Geräteeinstellung.
    const followSystem = () => {
      if (stored() !== null) return;
      root.dataset.reading = systemReading();
      hideAsk();
      sync();
    };
    if (systemQuery && typeof systemQuery.addEventListener === 'function') systemQuery.addEventListener('change', followSystem);

    // Der Kopf bleibt im Dokumentfluss. Erst wenn er vollständig oberhalb des
    // Viewports liegt, erscheint derselbe Darstellungswechsel am rechten Rand.
    const header = document.querySelector('.wa-header');
    const dock = document.querySelector('[data-reading-dock]');
    if (header && dock && button) {
      // Meldet der Navigation (public-navigation.js), ob die Leiste steht: Ihr Menü hängt dann in der Leiste.
      const announceDock = docked => {
        if (typeof document.dispatchEvent !== 'function' || typeof CustomEvent !== 'function') return;
        document.dispatchEvent(new CustomEvent('wa-reading-dock', { detail: { docked } }));
      };
      const updateDock = () => {
        const visible = header.getBoundingClientRect().bottom <= 0;
        if (visible === !dock.hidden) return;
        // Vor dem Verbergen: eine offene Menüfläche samt Fokus zuerst an den Kopf zurückgeben.
        if (!visible) announceDock(false);
        if (!visible && dock.contains(document.activeElement)) {
          // Der bisher fokussierte Schalter darf beim Zurückscrollen nicht
          // verschwinden. Schmal kann sein Gegenstück im geschlossenen Menü liegen.
          // Geschlossene <details> können trotzdem Rechtecke für ihre Kinder
          // liefern. Das Menü muss deshalb ausdrücklich berücksichtigt werden.
          // Der Menüknopf der Leiste hat sein Gegenstück im Auslöser des Kopfs.
          const active = document.activeElement;
          const onDockMenu = !!active && typeof active.matches === 'function' && active.matches('[data-nav-dock-toggle]');
          const target = onDockMenu || button.closest('details:not([open])') ? header.querySelector('.wa-menu-toggle') : button;
          // Bei erst teilweise sichtbarem Kopf auch das Fokusziel in Sicht holen.
          target?.focus();
        }
        dock.hidden = !visible;
        root.toggleAttribute('data-reading-docked', visible);
        // Nach dem Erscheinen: eine offene Menüfläche folgt in die Leiste.
        if (visible) announceDock(true);
      };
      refreshDock = updateDock;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(updateDock, { threshold: 0 }).observe(header);
      } else {
        // Gleiche Geometrie als Rückfall, kein fest angenommener Scrollabstand.
        let pending = false;
        const schedule = () => {
          if (pending) return;
          pending = true;
          requestAnimationFrame(() => { pending = false; updateDock(); });
        };
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
      }
      window.addEventListener('pageshow', updateDock);
      updateDock();
    }
    // Andere öffentliche Tabs und aus dem bfcache zurückgeholte Seiten folgen derselben Wahl.
    window.addEventListener('storage', event => {
      if (event.key !== key && event.key !== null) return;
      root.dataset.reading = readPreference();
      sync();
    });
    window.addEventListener('pageshow', () => {
      root.dataset.reading = readPreference();
      sync();
    });
    sync();
  });
})();
